/**
 * <s-variant-media> — brings the selected variant's image to the front of the product gallery.
 *
 * Contract:
 *   - Wraps the gallery; slides carry data-media-id="{{ media.id }}".
 *   - A <script type="application/json" data-variant-media> child maps variant id → media id.
 *   - data-current-variant holds the variant rendered by the server (?variant= deep links).
 *   - Listens for bubbling `change` events from the variant picker, whose inputs/options
 *     carry data-variant-id (see blocks/variant-picker.liquid).
 */
class SVariantMedia extends HTMLElement {
  #ac = null;
  #map = {};

  connectedCallback() {
    this.#ac = new AbortController();
    try {
      this.#map = JSON.parse(this.querySelector('[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#map = {};
    }

    this.#show(this.dataset.currentVariant, false);

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement) || !input.closest('[data-product-form]')) return;
      const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.#show(variantId, true);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #show(variantId, scroll) {
    const mediaId = this.#map[variantId];
    if (!mediaId) return;
    const gallery = this.querySelector('[data-product-gallery]');
    const slide = gallery?.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!gallery || !slide) return;

    if (gallery.firstElementChild !== slide) gallery.prepend(slide);
    if (!scroll) return;

    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    gallery.scrollTo({ left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (gallery.getBoundingClientRect().top < 0) {
      gallery.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('s-variant-media')) customElements.define('s-variant-media', SVariantMedia);
